<template>
	<div ref="pinWrapper" class="v-otp-input">
		<v-text-field
				v-for="(_, idx) in inputs"
				v-model="inputs[idx]"
				autocomplete="off"
				:key="idx"
				ref="pinInput"
				class="otp rounded-lg"
				:class="{ 'required': required }"
				:error-messages="idx === 0 ? errorMessages : null"
				:dense="dense || null"
				:disabled="disabled || null"
				:hide-details="hideDetails"
				:label="idx === 0 ? 'PIN' : null"
				maxlength="1"
				outlined
				:readonly="readonly || null"
				:rules="getRules(idx)"
				@focus="handleFocus(idx)"
				@input="handleInput(idx)"
				@keydown="handleKeyDown($event, idx)"
				@keyup.backspace="handleBackspace(idx, $event)"
				@keyup.delete="handleBackspace(idx, $event)"
		></v-text-field>
	</div>
</template>

<script>
export default {
	name: 'BwPinInput',
	inheritAttrs: false,
	props: {
		dense: Boolean,
		disabled: Boolean,
		hideDetails: [String, Boolean],
		length: {
			type: [Number, String],
			default: 4
		},
		readonly: Boolean,
		required: Boolean,
		value: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			errorMessages: [],
			isValid: false,
			// Add any other PINs we'd like to disallow here
			disallowedPINs: ['1234'],
			error: '',
			inputs: Array(this.length).fill(''),
			rules: {
				primary: [
					v => this.inputs.every(input => /^\d+$/.test(input)) || (this.required
							? 'PIN number is required'
							: 'PIN number is invalid'),
					v => !this.disallowedPINs.includes(this.inputs.join('')) || `PIN number '${this.inputs.join('')}' is not allowed`
				],
				secondary: [
					// Rule for all but the first input to show there's an error on the input
					v => this.inputs.every(input => input !== ''),
					v => !this.disallowedPINs.includes(this.inputs.join(''))
				]
			}
		};
	},
	beforeMount() {
		this.updateNumbers();
	},
	mounted() {
		if(this.disallowedPINs.includes(this.value) && !this.disabled && !this.readonly) {
			this.errorMessages = [`PIN number '${this.value}' is not allowed`];
		}
	},
	methods: {
		focusInput(idx) {
			const input = this.$refs.pinInput[idx];
			input.focus();
			return input.$refs.input.select();
		},
		getRules(idx) {
			const {
				inputs,
				required,
				rules: {
					primary,
					secondary
				}
			} = this;

			if(!required && inputs.every(input => input === '')) {
				return [];
			}

			return idx === 0 ? primary : secondary;
		},
		handleBackspace(idx, event) {
			if(event.key === "Backspace" || event.key === "Delete") {
				if(this.inputs[idx].length === 0 && idx > 0) {
					// Move focus to the previous field and delete the value
					this.focusInput(idx - 1);
					this.inputs[idx - 1] = "";

					this.$emit('input', this.inputs.join(''));
				}
				event.preventDefault();
			}
		},
		handleFocus(idx) {
			this.errorMessages = [];
			const emptyIndex = this.inputs.findIndex(input => input === '');

			this.$nextTick(() => {
				if(emptyIndex !== -1 && emptyIndex < idx) {
					this.focusInput(emptyIndex);
				}
				else {
					this.focusInput(idx);
				}
			});
		},
		handleInput(idx) {
			if(this.inputs[idx].length === 1 && idx < this.length - 1) {
				this.focusInput(idx + 1);
			}

			this.$refs.pinInput.forEach(input => input.validate());

			// Clear all input values to the right of the current field
			for (let i = idx + 1; i < this.length; i++) {
				this.inputs[i] = "";
			}

			return this.$emit('input', this.inputs.join(''));
		},
		handleKeyDown(e, idx) {
			const keyCode = e.keyCode;
			const isNumber = keyCode >= 48 && keyCode <= 57; // 0-9
			const isNumpad = keyCode >= 96 && keyCode <= 105; // Numpad 0-9
			const isControl = [8, 9, 37, 39, 46].includes(keyCode); // Backspace, Tab, Left, Right, Delete
			const isF5 = keyCode === 116; // F5 key
			const isR = keyCode === 82; // R key
			const isCommandOrControl = e.ctrlKey || e.metaKey; // Control (Windows/Linux) or Command (Mac)
			const isShift = e.shiftKey;

			// If it's the F5 key, allow the default behavior (refresh)
			if(isF5) {
				return;
			}

			// If it's the Control/Command + R or Control/Command + Shift + R, allow the default behavior (refresh)
			if(isCommandOrControl && isR) {
				return;
			}

			if((!isNumber && !isNumpad && !isControl) || (this.inputs[idx].length >= 1 && !isControl)) {
				e.preventDefault();
			}
		},
		updateNumbers() {
			if(this.value) {
				const valueArray = this.value.split('');
				this.inputs = Array.from({ length: this.length }, (
						_,
						index
				) => valueArray[index] || '');
			} else {
				this.inputs = [...this.inputs.fill('')];
			}
		},
		validate() {
		}
	},
	watch: {
		value() {
			this.updateNumbers();
		}
	}

};
</script>

<style lang="scss" scoped>
::v-deep .v-input[class*="--outlined"].otp {
	padding: 0 0.25rem !important;

	& input {
		padding: 0 !important;
		padding-top: 12px !important;
		font-size: 2rem !important;
		font-weight: 300 !important;

		& + [data-dashlanecreated] {
			display: none !important;
		}
	}

	&.v-input--dense input {
		font-size: 1.25rem !important;
	}

	& .v-messages {
		position: absolute !important;

		& .v-messages__message {
			white-space: nowrap !important;
		}
	}

	.v-label {
		transform: translateY(-12px) scale(0.75) !important;
		max-width: unset;
	}
}
</style>
